'use client'

import React, { useEffect, useRef, type RefObject } from 'react'
import { createPortal } from 'react-dom'

import { useMapHoverCardPosition } from '@/components/Location/useMapHoverCardPosition'
import { mapHoverCardMaxWidthClassName } from '@/components/Location/MapHoverCardShell'
import { cn } from '@/utilities/ui'

type MapHoverCardPortalProps = {
  anchorRef: RefObject<HTMLElement | null>
  children: React.ReactNode
  isActive: boolean
  onClose?: () => void
  onFocus?: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  positionDeps?: unknown[]
}

export function MapHoverCardPortal({
  anchorRef,
  children,
  isActive,
  onClose,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  positionDeps = [],
}: MapHoverCardPortalProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const placement = useMapHoverCardPosition({
    anchorRef,
    cardRef,
    deps: positionDeps,
    isActive,
  })

  useEffect(() => {
    if (!isActive || !onClose) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }

      event.preventDefault()
      onClose()
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isActive, onClose])

  if (!isActive) {
    return null
  }

  return createPortal(
    <div
      ref={cardRef}
      className={cn(
        'nw-map-overlay-layer pointer-events-auto fixed -translate-x-1/2',
        mapHoverCardMaxWidthClassName,
        placement?.placement === 'above' ? '-translate-y-full' : undefined,
        !placement && 'invisible',
      )}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        left: placement?.left ?? 0,
        top: placement?.top ?? 0,
      }}
    >
      {children}
    </div>,
    document.body,
  )
}
